import { useState } from "react";
import { EditIcon, EllipsisIcon, TrashIcon } from "lucide-react";

import { CreateCompanyCommentForm } from "./CreateCompanyCommentForm";
import { UpdateCompanyCommentForm } from "./UpdateCompanyCommentForm";
import {
  Column,
  ErrorText,
  LoadingIndicator,
  RichTextDisplay,
  Row,
  Text,
  VerificationBadge,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useDeleteCompanyComment } from "@/hooks/mutations/useDeleteCompanyComment";
import { useGetCompanyComments } from "@/hooks/queries/useGetCompanyComments";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetUser } from "@/hooks/queries/useGetUser";
import { useRemoveCompanyComment } from "@/hooks/sets/useRemoveCompanyComment";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { tiptap } from "@/lib/tiptap";
import { CompanyComment as CompanyCommentType, Role } from "@/types";

const jsonToHtml = tiptap.jsonToHtmlFn();

const CompanyComment = ({
  companyComment,
}: {
  companyComment: CompanyCommentType;
}) => {
  const {
    data: user,
    isPending: isUserPending,
    isError: isUserError,
  } = useGetUser({
    userId: companyComment.userId,
  });
  const { mutate: deleteCompanyComment } = useDeleteCompanyComment();
  const removeCompanyComment = useRemoveCompanyComment();
  const [isEditing, setIsEditing] = useState(false);
  const currentUser = useGetCurrentUser();
  if (!currentUser) {
    return null;
  }

  const renderUserInfo = () => {
    if (isUserPending) {
      return (
        <Row className="items-center gap-2">
          <Row className="h-6 w-6 items-center justify-center rounded-full bg-slate-200">
            <LoadingIndicator variant="small" />
          </Row>
          <p className="text-sm font-medium text-slate-400">Loading...</p>
        </Row>
      );
    } else if (isUserError) {
      return (
        <ErrorText
          errorMessage={`Unknown User • ${dates.timeAgoForPipeline(companyComment.createdAt)}`}
        />
      );
    }
    return (
      <Row className="items-center gap-2">
        <Row className="relative">
          <Row className="h-6 w-6 items-center justify-center rounded-full bg-slate-200">
            <p className="text-[10px] font-medium text-slate-900">
              {user.firstName[0]}
              {user.lastName[0]}
            </p>
          </Row>
          {user.role === Role.STAFFER && (
            <VerificationBadge className="absolute right-[-6px] top-[15px]" />
          )}
        </Row>
        <Text variant="small">
          {user.firstName} {user.lastName} •{" "}
          {dates.timeAgoForPipeline(companyComment.createdAt)}
        </Text>
      </Row>
    );
  };

  // Only show edit/delete dropdown if user is loaded and current user is the author
  const canEditOrDelete = currentUser.id === companyComment.userId;

  const render = () => {
    if (isEditing) {
      return (
        <UpdateCompanyCommentForm
          companyCommentId={companyComment.id}
          content={companyComment.content}
          onCancel={() => {
            setIsEditing(false);
          }}
          onSuccess={() => {
            setIsEditing(false);
          }}
        />
      );
    }
    return (
      <Column className="gap-2">
        <Row className="items-center justify-between">
          <Column className="gap-2">{renderUserInfo()}</Column>
          {canEditOrDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button size="icon" variant="ghost">
                  <EllipsisIcon className="h-4 w-4 text-slate-700" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[110px] p-0">
                <DropdownMenuItem
                  onClick={() => {
                    setIsEditing(true);
                  }}
                >
                  <EditIcon className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-500"
                  onClick={() => {
                    mixpanelUtils.trackButtonClick(
                      "Delete comment",
                      MIXPANEL_SCREENS.COMPANY,
                    );
                    deleteCompanyComment(
                      {
                        companyCommentId: companyComment.id,
                      },
                      {
                        onSuccess: (deletedCompanyComment) => {
                          removeCompanyComment({
                            companyCommentId: deletedCompanyComment.id,
                          });
                        },
                      },
                    );
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                  Remove
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </Row>
        <div className="whitespace-pre-line text-sm text-slate-700">
          <RichTextDisplay richText={jsonToHtml(companyComment.content)} />
        </div>
      </Column>
    );
  };

  return render();
};

export const CompanyComments = ({ className }: { className?: string }) => {
  const {
    data: comments = [],
    isPending: isCommentsPending,
    isError: isCommentsError,
  } = useGetCompanyComments();

  const render = () => {
    if (isCommentsPending) {
      return <LoadingIndicator variant="medium" />;
    } else if (isCommentsError) {
      return (
        <Column className="h-full items-center justify-center">
          <ErrorText errorMessage="An error occurred while loading comments. Please let the engineers know if they are not already on it!" />
        </Column>
      );
    }
    return (
      <Column className={className}>
        <CreateCompanyCommentForm />
        <Column className="gap-4">
          {comments.map((comment) => (
            <CompanyComment key={comment.id} companyComment={comment} />
          ))}
        </Column>
      </Column>
    );
  };

  return render();
};
